import { api } from '../lib/api';

export interface FlavorMetaProduct {
  id: number;
  name: string;
  kind: string;
  canPurchase: number;
  canSell: number;
  uomLabel: string | null;
  categoryLabel: string | null;
}

export interface FlavorSourceContainer {
  id: number;
  containerCode: string;
  siteId: number;
  siteName: string;
  productId: number;
  productName: string;
  remainingKg: number;
}

export interface FlavorMeta {
  sites: Array<{
    id: number;
    name: string;
  }>;
  sourceContainers: FlavorSourceContainer[];
  ingredientProducts: FlavorMetaProduct[];
  outputProducts: FlavorMetaProduct[];
}

export interface FlavorRecipeListRow {
  id: number;
  name: string;
  baseHoneyKg: number;
  outputProductId: number;
  outputProductName: string;
  notes: string | null;
  itemCount: number;
  isActive: number;
}

export interface FlavorRecipeDetail {
  id: number;
  name: string;
  baseHoneyKg: number;
  outputProductId: number;
  outputProductName: string;
  notes: string | null;
  isActive: number;
  items: Array<{
    id: number;
    productId: number;
    qtyPerBase: number;
    unitLabel: string;
    sortOrder: number;
    productName: string;
    sku: string | null;
    productUomLabel: string | null;
  }>;
}

export interface FlavorMixPreview {
  recipeId: number;
  recipeName: string;
  baseHoneyKg: number;
  outputProductId: number;
  outputProductName: string;
  sourceContainerId: number;
  sourceContainerCode: string;
  sourceProductName: string;
  siteId: number;
  siteName: string;
  sourceRemainingKg: number;
  plannedHoneyKg: number;
  factor: number;
  honeyEnough: boolean;
  items: Array<{
    productId: number;
    productName: string;
    unitLabel: string;
    qtyPerBase: number;
    plannedQty: number;
    onHand: number;
    enoughStock: boolean;
    sortOrder: number;
  }>;
}

export interface FlavorMixSourceRow {
  containerId: number;
  containerCode: string;
  siteId: number;
  siteName: string;
  productId: number | null;
  productName: string;
  qtyKg: number;
  sortOrder: number;
}

export interface FlavorMixListRow {
  id: number;
  mixDate: string;
  siteId: number;
  recipeId: number;
  sourceContainerId: number;
  sourceBlendId: number | null;
  plannedHoneyKg: number;
  yieldKg: number;
  status: 'DRAFT' | 'POSTED';
  outputContainerId: number | null;
  inventoryTxId: number | null;
  comment: string | null;
  recipeName: string;
  sourceContainerCode: string;
  siteName: string;
  outputContainerCode: string | null;
  outputProductName: string | null;
  itemCount: number;
  sourceCount: number;
}

export interface FlavorMixDetail {
  id: number;
  mixDate: string;
  siteId: number;
  recipeId: number;
  sourceContainerId: number;
  sourceBlendId: number | null;
  plannedHoneyKg: number;
  yieldKg: number;
  spillKg: number;
  status: 'DRAFT' | 'POSTED';
  outputContainerId: number | null;
  inventoryTxId: number | null;
  comment: string | null;
  postedAt: string | null;
  recipeName: string;
  baseHoneyKg: number;
  outputProductId: number;
  outputProductName: string;
  sourceContainerCode: string;
  sourceProductName: string;
  siteName: string;
  outputContainerCode: string | null;
  sourceContainers: FlavorMixSourceRow[];
  items: Array<{
    id: number;
    productId: number;
    plannedQty: number;
    unitLabel: string;
    sortOrder: number;
    productName: string;
  }>;
}

export const flavorApi = {
  meta: () => api.get<FlavorMeta>('/flavor/meta'),
  listRecipes: () => api.get<{ recipes: FlavorRecipeListRow[] }>('/flavor-recipes'),
  getRecipe: (id: number) => api.get<FlavorRecipeDetail>(`/flavor-recipes/${id}`),
  createRecipe: (payload: {
    name: string;
    baseHoneyKg: number;
    outputProductId: number;
    notes?: string;
    items: Array<{ productId: number; qtyPerBase: number; unitLabel: string }>;
  }) => api.post<{ id: number }>('/flavor-recipes', payload),
  updateRecipe: (
    id: number,
    payload: {
      name: string;
      baseHoneyKg: number;
      outputProductId: number;
      notes?: string;
      items: Array<{ productId: number; qtyPerBase: number; unitLabel: string }>;
    }
  ) => api.put<{ ok: boolean }>(`/flavor-recipes/${id}`, payload),
  deleteRecipe: (id: number) => api.delete<{ ok: boolean }>(`/flavor-recipes/${id}`),
  previewMix: (payload: {
    recipeId: number;
    siteId: number;
    plannedHoneyKg: number;
    sourceContainers: Array<{ containerId: number; qtyKg: number }>;
  }) => api.post<FlavorMixPreview>('/flavor-mixes/preview', payload),
  listMixes: (year: number) => api.get<{ mixes: FlavorMixListRow[] }>(`/flavor-mixes?year=${year}`),
  getMix: (id: number) => api.get<FlavorMixDetail>(`/flavor-mixes/${id}`),
  createMix: (payload: {
    recipeId: number;
    siteId: number;
    sourceContainers: Array<{ containerId: number; qtyKg: number }>;
    mixDate?: string;
    comment?: string;
  }) => api.post<{ id: number }>('/flavor-mixes', payload),
  updateMix: (
    id: number,
    payload: {
      recipeId: number;
      siteId: number;
      sourceContainers: Array<{ containerId: number; qtyKg: number }>;
      mixDate?: string;
      comment?: string;
    }
  ) => api.patch<{ ok: boolean }>(`/flavor-mixes/${id}`, payload),
  updateYield: (id: number, payload: { yieldKg: number }) => api.patch<{ ok: boolean }>(`/flavor-mixes/${id}/yield`, payload),
  postMix: (id: number) =>
    api.post<{ ok: boolean; outputContainerId: number; outputContainerCode: string; inventoryTxId: number }>(`/flavor-mixes/${id}/post`),
};
